/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { SupportedLocale } from '../../model.js';
import { localize } from '../../translations.js';
import * as Assets from '../../assets.js';
import { ModalAction } from './pwc-modal.js';
import { unreacheable } from '../../internal/unreacheable.js';
/**
 * States.
 */
export const FeedbackModalState = {
    CLOSED: 'CLOSED',
    PROVIDE_FEEDBACK: 'PROVIDE-FEEDBACK',
    FEEDBACK_PROVIDED: 'FEEDBACK-PROVIDED',
};
/**
 * WebComponent.
 */
let PwcFeedbackModal = class PwcFeedbackModal extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Properties.
         */
        this.locale = SupportedLocale.en_US;
        this.state = FeedbackModalState.CLOSED;
        /**
         * States.
         */
        this._isSubmitDisabled = true;
    }
    /**
     * Render "Provide Feedback" state.
     */
    _renderProvideFeedback() {
        return html `
      <pwc-modal
        has-submit-button
        has-cancel-button
        .isSubmitDisabled=${this._isSubmitDisabled}
        .locale=${this.locale}
        .modalTitle=${localize(this.locale, 'ai.chat.provide-feedback')}
        @pwc-modal-action-event=${(event) => {
            this._handleModalAction(event);
        }}
      >
        <div class="provide-feedback">
          <div class="label">
            <div class="text">
              ${localize(this.locale, 'ai.chat.provide-feedback.input-label')}&nbsp;
            </div>
          </div>
          <textarea
            @keyup=${() => this._handleTextAreaChange()}
            placeholder=${localize(this.locale, 'ai.chat.provide-feedback.input-placeholder')}
          ></textarea>
        </div>
      </pwc-modal>
    `;
    }
    /**
     * Render "Thanks For Your Feedback" state.
     */
    _renderFeedbackProvided() {
        return html `
      <pwc-modal
        .locale=${this.locale}
        @pwc-modal-action-event=${(event) => {
            this._handleModalAction(event);
        }}
      >
        <div class="feedback-provided">
          <div class="asset">${Assets.imageFeedback()}</div>
          <div class="title">
            ${localize(this.locale, 'ai.chat.feedback-provided.title')}
          </div>
          <div class="subtitle">
            ${localize(this.locale, 'ai.chat.feedback-provided.subtitle')}
          </div>
        </div>
      </pwc-modal>
    `;
    }
    /**
     * @inheritdoc
     */
    render() {
        switch (this.state) {
            case FeedbackModalState.FEEDBACK_PROVIDED:
                return this._renderFeedbackProvided();
            case FeedbackModalState.PROVIDE_FEEDBACK:
                return this._renderProvideFeedback();
            case FeedbackModalState.CLOSED:
                return html ``;
            default:
                unreacheable(this.state);
        }
        return html ``;
    }
    /**
     * Handle textarea change.
     */
    _handleTextAreaChange() {
        // guard.
        if (!this._textarea) {
            return;
        }
        // get the user content.
        const content = this._textarea.value.trim();
        // toggle submit state accordingly.
        if (content.length > 0) {
            this._isSubmitDisabled = false;
        }
        else {
            this._isSubmitDisabled = true;
        }
    }
    /**
     * Handle the modal action.
     */
    _handleModalAction(event) {
        // get the action.
        const { action } = event.detail;
        // handle it.
        switch (action) {
            case ModalAction.CANCEL:
            case ModalAction.CLOSE:
                this._handleCloseFeedbackModal();
                break;
            case ModalAction.SUBMIT:
                this._handleSubmitFeedback();
                break;
            default:
                unreacheable(action);
        }
    }
    /**
     * Handle the close modal.
     */
    _handleCloseFeedbackModal() {
        // create custom event.
        const event = new CustomEvent('pwc-feedback-modal-close-event');
        // dispatch it.
        this.dispatchEvent(event);
    }
    /**
     * Handle the submit feedback.
     */
    _handleSubmitFeedback() {
        // guard.
        if (!this._textarea) {
            return;
        }
        // create custom event.
        const event = new CustomEvent('pwc-feedback-modal-submit-event', {
            detail: {
                feedbackComment: this._textarea.value,
            },
        });
        // dispatch it.
        this.dispatchEvent(event);
    }
};
// styles.
PwcFeedbackModal.styles = css `
    :host {
      --pwc-feedback-modal-font-size-medium: var(--pwc-font-size-medium, 1rem);
    }

    /**
     * Feedback Provided.
     */
    .feedback-provided {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0px 30px 30px 30px;
      font-size: var(--pwc-feedback-modal-font-size-medium);
      max-width: 480px;
      box-sizing: border-box;
    }

    .feedback-provided .image-feedback {
      width: 342px;
      height: 198px;
    }

    .feedback-provided .title {
      margin-top: 20px;
      font-weight: 500;
      text-align: center;
      color: #212121;
    }

    .feedback-provided .subtitle {
      margin-top: 10px;
      color: #707070;
      text-align: center;
    }

    /**
     * Provide Feedback.
     */
    .provide-feedback {
      padding: 15px 20px;
      font-size: var(--pwc-feedback-modal-font-size-medium);
      display: flex;
      flex-direction: column;
      max-width: 480px;
      box-sizing: border-box;
    }

    .provide-feedback .label {
      margin-top: 10px;
      margin-bottom: 10px;
      font-weight: 500;
      text-align: left;
      color: #212121;
      display: inline-block;
    }

    .provide-feedback .label .text {
      float: left;
    }

    .provide-feedback textarea {
      border: 1px solid #e0e0e0;
      resize: none;
      padding: 8px;
      border-radius: 3px;
      min-height: 4rem;
      font-size: var(--pwc-font-size-medium, 1rem);
      font-family: inherit;
      margin-bottom: 10px;
      max-width: calc(100% - 20px);
      width: 480px;
    }

    .provide-feedback textarea::placeholder {
      font-size: var(--pwc-feedback-modal-font-size-medium);
    }
  `;
__decorate([
    property({ attribute: 'locale', type: String })
], PwcFeedbackModal.prototype, "locale", void 0);
__decorate([
    property({ attribute: false })
], PwcFeedbackModal.prototype, "state", void 0);
__decorate([
    query('.provide-feedback textarea', false)
], PwcFeedbackModal.prototype, "_textarea", void 0);
__decorate([
    state()
], PwcFeedbackModal.prototype, "_isSubmitDisabled", void 0);
PwcFeedbackModal = __decorate([
    customElement('pwc-feedback-modal')
], PwcFeedbackModal);
export { PwcFeedbackModal };
//# sourceMappingURL=pwc-feedback-modal.js.map